import { applyMiddleware, combineReducers, compose, createStore, type Reducer } from 'redux'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import type { AppState } from '~/types'
import CalendarReducer from './calendar'
import LocaleReducer from './locale'
import ThemeReducer from './theme'

export * from './calendar'
export * from './locale'
export * from './theme'

/**
 * The application's own Redux store.
 *
 * react-admin 5 has no Redux store of its own — it uses React context and
 * react-query — so everything that used to be wired in here is gone:
 * `adminReducer`, `adminSaga`, `connected-react-router` and the `history` that
 * fed it, plus the CLEAR_STATE reset that reached into `state.admin`.
 *
 * What remains is genuinely ours: the calendar's view and date range, the
 * chosen locale, and the theme. Those are persisted, which is why the store
 * still exists at all rather than being replaced by context.
 *
 * Consumers of the old `state.admin.ui.sidebarOpen` should use react-admin's
 * `useSidebarState()` instead.
 */
// AppState lives in ~/types, where every consumer already imports it from.
export type { AppState } from '~/types'

export default () => {
  const appReducer = combineReducers({
    theme: ThemeReducer,
    locale: LocaleReducer,
    calendar: CalendarReducer,
  })

  const typedWindow = typeof window !== 'undefined' && (window as Window)
  const composeEnhancers =
    (import.meta.env.DEV &&
      typeof typedWindow !== 'undefined' &&
      // @ts-ignore — devtools extension is untyped
      typedWindow.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
      // @ts-ignore
      typedWindow.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true, traceLimit: 25 })) ||
    compose

  // redux 5's Reducer gained a preloaded-state type parameter that redux-persist
  // 6's typings predate; the cast only reconciles the two declarations.
  const persistedReducer = persistReducer(
    { key: 'root', storage },
    appReducer as unknown as Reducer<AppState>
  )
  const store = createStore(persistedReducer, composeEnhancers(applyMiddleware()))

  return { store, persistor: persistStore(store) }
}
